/**
 * My Bookings Page
 * Display user's booking history
 */

import { t, formatDate, formatCurrency, getCurrentLanguage, type Language } from '../i18n';
import { Header, Card, AppVersionFooter, BackToTopButton, initBackToTop } from '../components';
import { apiFetch } from '../api';
import { getProfile, closeWindow } from '../liff';
import { navigateTo, loadSavedGuestInfo } from '../store';
import type { Booking } from '../types';

/**
 * Show success toast notification (instead of alert to avoid domain name)
 */
function showSuccessToast(message: string): void {
  showToast(message, 'success');
}

/**
 * Show error toast notification
 */
function showErrorToast(message: string): void {
  showToast(message, 'error');
}

/**
 * Show toast notification
 */
function showToast(message: string, type: 'success' | 'error' = 'success'): void {
  // Remove existing toast
  const existingToast = document.getElementById('custom-toast');
  if (existingToast) existingToast.remove();

  const toast = document.createElement('div');
  toast.id = 'custom-toast';
  toast.style.cssText = `
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: ${type === 'success' ? '#00B900' : '#dc3545'};
    color: white;
    padding: 20px 32px;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    box-shadow: 0 8px 32px rgba(0,0,0,0.3);
    z-index: 99999;
    text-align: center;
    max-width: 80%;
    animation: toastFadeIn 0.3s ease;
  `;
  toast.innerHTML = `
    <div style="font-size: 32px; margin-bottom: 8px;">${type === 'success' ? '✅' : '❌'}</div>
    <div>${message}</div>
  `;

  // Add animation style
  const style = document.createElement('style');
  style.textContent = `
    @keyframes toastFadeIn {
      from { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
      to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
    }
    @keyframes toastFadeOut {
      from { opacity: 1; transform: translate(-50%, -50%) scale(1); }
      to { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
    }
  `;
  document.head.appendChild(style);
  document.body.appendChild(toast);

  // Auto remove after 2 seconds
  setTimeout(() => {
    toast.style.animation = 'toastFadeOut 0.3s ease forwards';
    setTimeout(() => toast.remove(), 300);
  }, 2000);
}

/**
 * Render my bookings page
 */
export function renderMyBookingsPage(): string {
  return `
    ${Header(t('myBookings'), false)}
    
    <div class="page-content">
      <div id="bookings-list">
        <div style="text-align: center; padding: 60px 20px;">
          <div class="spinner"></div>
          <p style="margin-top: 20px; color: #666;">${t('loading')}</p>
        </div>
      </div>
    </div>

    ${BackToTopButton('mybookings')}
  `;
}

/**
 * Initialize my bookings page
 */
export async function initMyBookingsPage(): Promise<void> {
  console.log('[MyBookings] Initializing...');

  const container = document.getElementById('bookings-list');
  if (!container) {
    console.error('[MyBookings] Container not found');
    return;
  }

  try {
    // 1. Resolve user profile gracefully (Store -> LIFF SDK -> Saved Guest -> Persistent Web ID)
    const { getState, loadSavedGuestInfo } = await import('../store');
    const storeUser = getState().user;
    let profile = storeUser;

    if (!profile || !profile.userId) {
      try {
        profile = await getProfile();
      } catch (e) {
        console.warn('[MyBookings] LIFF getProfile error, falling back:', e);
      }
    }

    const savedGuest = loadSavedGuestInfo();

    if (!profile || !profile.userId) {
      const fallbackId = savedGuest?.phone
        ? `guest_${savedGuest.phone.replace(/\D/g, '')}`
        : (typeof localStorage !== 'undefined' ? (localStorage.getItem('revio_web_user_id') || `guest_${Date.now()}`) : `guest_${Date.now()}`);
      profile = {
        userId: fallbackId,
        displayName: savedGuest?.firstName ? `${savedGuest.firstName} ${savedGuest.lastName || ''}`.trim() : 'Guest',
        language: (getCurrentLanguage() || 'th') as any,
      };
    }

    console.log('[MyBookings] Resolved User ID:', profile.userId);

    // 2. Resolve tenant ID safely
    const { getTenantId, ensureTenantResolved } = await import('../config');
    let tenantId = getTenantId();
    if (!tenantId) {
      tenantId = await ensureTenantResolved();
    }
    console.log('[MyBookings] Tenant ID:', tenantId);

    // If tenant could not be resolved, provide manual lookup instead of hard error
    if (!tenantId) {
      renderNoBookings();
      return;
    }

    // 3. Prepare query parameters for omnichannel booking lookup
    const queryParams = new URLSearchParams();
    if (savedGuest?.phone) queryParams.set('phone', savedGuest.phone);
    if (savedGuest?.email) queryParams.set('email', savedGuest.email);

    // Read current channel to isolate bookings per platform
    const currentChannel = (typeof sessionStorage !== 'undefined' ? (sessionStorage.getItem('_channel') || localStorage.getItem('_channel')) : null) || (new URLSearchParams(window.location.search).get('channel')) || 'line';
    queryParams.set('channel', currentChannel);

    try {
      const channelCodes = JSON.parse(localStorage.getItem(`my_booking_codes_${currentChannel}`) || '[]');
      const legacyCodes = JSON.parse(localStorage.getItem('my_booking_codes') || '[]');
      const codes = Array.from(new Set([...channelCodes, ...legacyCodes]));
      if (Array.isArray(codes) && codes.length > 0) {
        queryParams.set('codes', codes.join(','));
      }
    } catch (_) {}
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';

    let result: {
      success: boolean;
      data: { lineUserId: string; totalBookings: number; bookings: Booking[]; cancellationDeadlineDays?: number };
    } | null = null;

    try {
      result = await apiFetch<{
        success: boolean;
        data: { lineUserId: string; totalBookings: number; bookings: Booking[]; cancellationDeadlineDays?: number };
      }>(`/api/v1/${tenantId}/bookings/user/${encodeURIComponent(profile.userId)}${queryString}`);
    } catch (apiErr: any) {
      console.warn('[MyBookings] Primary user lookup failed, attempting guest contact fallback:', apiErr);
      if (savedGuest?.phone || savedGuest?.email) {
        const fallbackTarget = savedGuest.phone || savedGuest.email || '';
        try {
          result = await apiFetch<{
            success: boolean;
            data: { lineUserId: string; totalBookings: number; bookings: Booking[]; cancellationDeadlineDays?: number };
          }>(`/api/v1/${tenantId}/bookings/user/${encodeURIComponent(fallbackTarget)}${queryString}`);
        } catch (_) {}
      }
    }

    console.log('[MyBookings] API result:', result);

    if (
      !result ||
      !result.success ||
      !result.data ||
      !result.data.bookings ||
      result.data.bookings.length === 0
    ) {
      console.log('[MyBookings] No bookings found');
      renderNoBookings();
      return;
    }

    console.log('[MyBookings] Rendering bookings:', result.data.bookings.length);
    const deadlineDays = result.data.cancellationDeadlineDays ?? 3;
    renderBookingsList(result.data.bookings, deadlineDays);

    // Floating "BACK TO TOP" for My Bookings
    initBackToTop('mybookings');
  } catch (error) {
    console.error('[MyBookings] Error:', error);

    // If unexpected error occurs, fall back to lookup box so user can still find bookings
    renderNoBookings();
  }
}

/**
 * Render no bookings message
 */
function renderNoBookings(): void {
  const container = document.getElementById('bookings-list');
  if (!container) return;

  container.innerHTML = `
    <div style="text-align: center; padding: 30px 20px;">
      <div style="font-size: 64px; margin-bottom: 16px;">📋</div>
      <h3 style="color: #333; margin-bottom: 8px; font-size: 18px; font-weight: 700;">${t('noBookings')}</h3>
      <p style="color: #666; font-size: 13px; margin-bottom: 24px;">${t('noBookingsDesc')}</p>

      <!-- Interactive Booking Lookup Box -->
      <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 18px 16px; margin: 0 auto 24px; max-width: 380px; text-align: left;">
        <label style="display: block; font-size: 12px; font-weight: 600; color: #475569; margin-bottom: 6px;">
          🔍 ค้นหาการจองด้วย เบอร์โทร / อีเมล / รหัสการจอง
        </label>
        <div style="display: flex; gap: 8px;">
          <input 
            type="text" 
            id="manual-booking-lookup-input" 
            placeholder="เช่น 0923488879 หรือ 2CUSLPRZ"
            style="flex: 1; padding: 10px 12px; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 13px; outline: none;"
          />
          <button 
            type="button" 
            id="manual-booking-lookup-btn"
            style="padding: 10px 16px; background: #059669; color: white; border: none; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; white-space: nowrap;"
          >
            ค้นหา
          </button>
        </div>
        <p id="lookup-error-msg" style="color: #e11d48; font-size: 12px; margin-top: 6px; display: none;"></p>
      </div>

      <div style="display: flex; flex-direction: column; gap: 10px; max-width: 380px; margin: 0 auto;">
        <button 
          type="button" 
          class="btn btn-primary" 
          onclick="window.navigateToSearch()"
        >
          ${t('startBooking')}
        </button>
        <button 
          type="button" 
          class="btn btn-orange" 
          onclick="window.closeMyBookings()"
        >
          ${t('close')}
        </button>
      </div>
    </div>
  `;

  // Attach search lookup handler
  const lookupBtn = document.getElementById('manual-booking-lookup-btn');
  const lookupInput = document.getElementById('manual-booking-lookup-input') as HTMLInputElement | null;
  const lookupError = document.getElementById('lookup-error-msg');

  const executeLookup = async () => {
    const query = lookupInput?.value.trim() || '';
    if (!query) {
      if (lookupError) {
        lookupError.textContent = 'กรุณาระบุเบอร์โทรศัพท์ อีเมล หรือรหัสการจอง';
        lookupError.style.display = 'block';
      }
      return;
    }

    if (lookupBtn) {
      lookupBtn.textContent = 'กำลังค้นหา...';
      (lookupBtn as HTMLButtonElement).disabled = true;
    }
    if (lookupError) lookupError.style.display = 'none';

    try {
      const { getTenantId } = await import('../config');
      const tenantId = getTenantId();
      const isEmail = query.includes('@');
      const isDigits = /^[\d+]+$/.test(query);

      const qp = new URLSearchParams();
      if (isEmail) {
        qp.set('email', query.toLowerCase());
      } else if (isDigits && query.replace(/\D/g, '').length >= 8) {
        qp.set('phone', query);
      } else {
        qp.set('codes', query.toUpperCase());
      }

      const res = await apiFetch<{
        success: boolean;
        data: { lineUserId: string; totalBookings: number; bookings: Booking[]; cancellationDeadlineDays?: number };
      }>(`/api/v1/${tenantId}/bookings/user/${encodeURIComponent(query)}?${qp.toString()}`);

      if (res.success && res.data?.bookings && res.data.bookings.length > 0) {
        // Save found identity for future auto-load
        try {
          if (isEmail) {
            const cur = loadSavedGuestInfo() || {};
            localStorage.setItem('lineoa_guest_info', JSON.stringify({ ...cur, email: query.toLowerCase(), savedAt: new Date().toISOString() }));
          } else if (isDigits) {
            const cur = loadSavedGuestInfo() || {};
            localStorage.setItem('lineoa_guest_info', JSON.stringify({ ...cur, phone: query, savedAt: new Date().toISOString() }));
          }
        } catch (_) {}

        renderBookingsList(res.data.bookings, res.data.cancellationDeadlineDays ?? 3);
      } else {
        if (lookupError) {
          lookupError.textContent = 'ไม่พบข้อมูลการจองที่ตรงกับข้อมูลที่ระบุ';
          lookupError.style.display = 'block';
        }
      }
    } catch (e: any) {
      if (lookupError) {
        lookupError.textContent = 'เกิดข้อผิดพลาดในการค้นหา: ' + (e.message || 'Network error');
        lookupError.style.display = 'block';
      }
    } finally {
      if (lookupBtn) {
        lookupBtn.textContent = 'ค้นหา';
        (lookupBtn as HTMLButtonElement).disabled = false;
      }
    }
  };

  lookupBtn?.addEventListener('click', executeLookup);
  lookupInput?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      executeLookup();
    }
  });

  window.navigateToSearch = () => {
    if (typeof (window as any).goHome === 'function') {
      (window as any).goHome();
    } else {
      navigateTo('search');
    }
  };

  window.closeMyBookings = () => {
    closeWindow();
  };
}

/**
 * Check if booking can be modified/cancelled
 * @param deadlineDays - min days before check-in required (from tenant settings, default 3)
 */
function canModifyBooking(checkInDate: string, deadlineDays: number = 3): boolean {
  const checkIn = new Date(checkInDate);
  const today = new Date();
  const diffTime = checkIn.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays >= deadlineDays;
}

// 6-Language Lookup Tables for Booking Details
const MICE_PAY_AT_VENUE: Record<Language, string> = {
  th: 'ชำระ ณ สถานที่ (Pay at Venue)',
  en: 'Pay at Venue',
  zh: '到店支付 (Pay at Venue)',
  ru: 'Оплата на месте (Pay at Venue)',
  ja: '現地決済 (Pay at Venue)',
  ko: '현장 결제 (Pay at Venue)',
};

const MICE_RFP: Record<Language, string> = {
  th: 'ขอใบเสนอราคา (Quotation on Request / RFP)',
  en: 'Quotation on Request (RFP)',
  zh: '申请定制报价 (RFP)',
  ru: 'Запрос ценового предложения (RFP)',
  ja: 'お見積もりリクエスト (RFP)',
  ko: '견적 요청 (RFP)',
};

const PAY_AT_SPA: Record<Language, string> = {
  th: 'ชำระเงินที่ร้าน (Pay at Spa)',
  en: 'Pay at Spa',
  zh: '到店支付 (Pay at Spa)',
  ru: 'Оплата в СПА (Pay at Spa)',
  ja: '店頭払い (Pay at Spa)',
  ko: '매장 결제 (Pay at Spa)',
};

const PAY_AT_RESTAURANT: Record<Language, string> = {
  th: 'ชำระเงินที่ร้าน (Pay at Restaurant)',
  en: 'Pay at Restaurant',
  zh: '到店支付 (Pay at Restaurant)',
  ru: 'Оплата в ресторане (Pay at Restaurant)',
  ja: '店頭払い (Pay at Restaurant)',
  ko: '매장 결제 (Pay at Restaurant)',
};

const DURATION_MINS: Record<Language, string> = {
  th: 'นาที',
  en: 'mins',
  zh: '分钟',
  ru: 'мин',
  ja: '分',
  ko: '분',
};

const LABEL_SPA: Record<Language, string> = {
  th: 'สปา',
  en: 'Spa',
  zh: '水疗中心',
  ru: 'СПА-центр',
  ja: 'スパ',
  ko: '스파',
};

const LABEL_TREATMENT: Record<Language, string> = {
  th: 'ทรีตเมนต์',
  en: 'Treatment',
  zh: '护理项目',
  ru: 'Процедура',
  ja: 'トリートメント',
  ko: '트리트먼트',
};

const LABEL_SERVICE_DATE: Record<Language, string> = {
  th: 'วันที่รับบริการ',
  en: 'Service Date',
  zh: '服务日期',
  ru: 'Дата услуги',
  ja: 'ご利用日',
  ko: '이용 일자',
};

const LABEL_TIME_SLOT: Record<Language, string> = {
  th: 'รอบเวลา',
  en: 'Time Slot',
  zh: '时间段',
  ru: 'Время сеанса',
  ja: 'ご利用時間',
  ko: '이용 시간',
};

const LABEL_ROOM_NUMBER: Record<Language, string> = {
  th: 'หมายเลขห้องพัก',
  en: 'Room Number',
  zh: '房间号',
  ru: 'Номер комнаты',
  ja: 'お部屋番号',
  ko: '객실 번호',
};

const formatRoomNumber = (roomNumber: string | number, l: Language) => {
  if (l === 'th') return `ห้อง ${roomNumber}`;
  if (l === 'zh') return `${roomNumber} 号房`;
  if (l === 'ja') return `${roomNumber}号室`;
  if (l === 'ko') return `${roomNumber}호실`;
  if (l === 'ru') return `Номер ${roomNumber}`;
  return `Room ${roomNumber}`;
};

const LABEL_RESTAURANT: Record<Language, string> = {
  th: 'ห้องอาหาร',
  en: 'Restaurant',
  zh: '餐厅',
  ru: 'Ресторан',
  ja: 'レストラン',
  ko: '레스토랑',
};

const LABEL_SEATING_AREA: Record<Language, string> = {
  th: 'โซนที่นั่ง',
  en: 'Seating Area',
  zh: '用餐区域',
  ru: 'Зона посадки',
  ja: 'お席エリア',
  ko: '좌석 구역',
};

const LABEL_PARTY_SIZE: Record<Language, string> = {
  th: 'จำนวนที่นั่ง',
  en: 'Guests / Party Size',
  zh: '用餐人数',
  ru: 'Количество гостей',
  ja: 'ご利用人数',
  ko: '인원 수',
};

const PARTY_UNIT: Record<Language, string> = {
  th: 'ท่าน',
  en: 'Guests',
  zh: '位',
  ru: 'гостей',
  ja: '名',
  ko: '명',
};

const MICE_VENUE: Record<Language, string> = {
  th: 'สถานที่จัดงาน',
  en: 'Function Room',
  zh: '会议场地',
  ru: 'Площадка / Конференц-зал',
  ja: '会場・会議室',
  ko: '행사장 / 회의실',
};

const MICE_LAYOUT: Record<Language, string> = {
  th: 'รูปแบบการจัดโต๊ะ',
  en: 'Seating Layout',
  zh: '摆台形式',
  ru: 'Схема рассадки',
  ja: 'レイアウト',
  ko: '좌석 배치',
};

const MICE_PACKAGE: Record<Language, string> = {
  th: 'แพ็กเกจ',
  en: 'Package',
  zh: '会议套餐',
  ru: 'Пакет мероприятия',
  ja: 'パッケージ',
  ko: '패키지',
};

const MICE_COMPANY: Record<Language, string> = {
  th: 'บริษัท/องค์กร',
  en: 'Company / Organization',
  zh: '公司/组织',
  ru: 'Компания / Организация',
  ja: '企業・団体名',
  ko: '회사 / 단체명',
};

const MICE_ORGANIZER: Record<Language, string> = {
  th: 'ผู้ประสานงาน',
  en: 'Organizer',
  zh: '联系人',
  ru: 'Контактное лицо',
  ja: '担当者名',
  ko: '담당자',
};

const MICE_EVENT_DATE: Record<Language, string> = {
  th: 'วันที่จัดงาน',
  en: 'Event Date',
  zh: '活动日期',
  ru: 'Дата мероприятия',
  ja: '開催日',
  ko: '행사 일자',
};

const MICE_TIME_SLOT: Record<Language, string> = {
  th: 'ช่วงเวลา',
  en: 'Time Slot',
  zh: '时间段',
  ru: 'Время сеанса',
  ja: '時間帯',
  ko: '시간대',
};

const MICE_ATTENDEES: Record<Language, string> = {
  th: 'จำนวนผู้เข้าร่วม',
  en: 'Attendees',
  zh: '参会人数',
  ru: 'Количество участников',
  ja: '参加人数',
  ko: '참석 인원',
};

const formatMicePax = (pax: number, l: Language) => {
  if (l === 'th') return `${pax} ท่าน (Pax)`;
  if (l === 'zh') return `${pax} 位 (Pax)`;
  if (l === 'ru') return `${pax} чел. (Pax)`;
  if (l === 'ja') return `${pax} 名 (Pax)`;
  if (l === 'ko') return `${pax} 명 (Pax)`;
  return `${pax} Pax`;
};

const MICE_RFP_STATUS_LABEL: Record<Language, string> = {
  th: 'สถานะใบเสนอราคา',
  en: 'Quotation Status',
  zh: '报价单状态',
  ru: 'Статус ценового предложения',
  ja: 'お見積もり状況',
  ko: '견적 현황',
};

const MICE_RFP_STATUS_TEXT: Record<Language, string> = {
  th: 'รอฝ่ายขายจัดทำใบเสนอราคา',
  en: 'Quotation Pending (RFP)',
  zh: '等待销售团队提供定制方案',
  ru: 'Ожидается расчет от отдела продаж',
  ja: '担当者が見積もりを作成中',
  ko: '영업팀 견적서 작성 대기 중',
};

const MICE_RFP_NOTICE: Record<Language, string> = {
  th: 'เจ้าหน้าที่ฝ่ายขายและการจัดเลี้ยง (Event Specialist) จะติดต่อประสานงานรายละเอียดการจัดงานโดยเร็วครับ',
  en: 'Our event specialist will contact you shortly to coordinate event details.',
  zh: '宴会销售专员将尽快与您联系，确认活动细节并提供定制方案。',
  ru: 'Наш специалист по мероприятиям свяжется с вами в ближайшее время для уточнения деталей.',
  ja: 'イベントプランナーより詳細確認のため折り返しご連絡いたします。',
  ko: '행사 전담 플래너가 상세 조율을 위해 곧 연락드리겠습니다.',
};

const resolveSeatingArea = (area: any, l: Language) => {
  if (typeof area === 'object' && area) {
    return area[l] || area.en || area.th || Object.values(area)[0] || '';
  }
  if (area === 'Indoor (แอร์)') {
    return l === 'en' ? 'Indoor (A/C)' : l === 'zh' ? '室内 (空调)' : l === 'ru' ? 'В помещении (кондиционер)' : l === 'ja' ? '屋内 (エアコン)' : l === 'ko' ? '실내 (에어컨)' : 'Indoor (แอร์)';
  }
  if (area === 'Outdoor (ริมสระ)') {
    return l === 'en' ? 'Outdoor (Poolside)' : l === 'zh' ? '室外 (泳池边)' : l === 'ru' ? 'На открытом воздухе (у бассейна)' : l === 'ja' ? '屋外 (プールサイド)' : l === 'ko' ? '야외 (수영장 옆)' : 'Outdoor (ริมสระ)';
  }
  if (area === 'VIP Room') {
    return l === 'en' ? 'VIP Room' : l === 'zh' ? 'VIP 贵宾房' : l === 'ru' ? 'VIP комната' : l === 'ja' ? 'VIPルーム' : l === 'ko' ? 'VIP 룸' : 'VIP Room';
  }
  return area || (l === 'en' ? 'Indoor' : 'ในร่ม');
};

/**
 * Render bookings list
 */
function renderBookingsList(bookings: Booking[], deadlineDays: number = 3): void {
  const container = document.getElementById('bookings-list');
  if (!container) return;

  const bookingsHtml = bookings
    .map(booking => {
      const statusColor =
        booking.status === 'confirmed'
          ? '#00B900'
          : booking.status === 'cancelled'
            ? '#999999'
            : '#FF9500';
      const statusMap: Record<string, string> = {
        confirmed: t('statusConfirmed'),
        cancelled: t('statusCancelled'),
        completed: t('statusCompleted'),
        pending: t('statusPending'),
      };
      const statusText = statusMap[booking.status] || booking.status;

      const statusIcon =
        booking.status === 'confirmed'
          ? '✓'
          : booking.status === 'cancelled'
            ? '✗'
            : booking.status === 'completed'
              ? '✓'
              : '⏳';

      return Card(
        `
      <div class="booking-card">
        <div class="booking-header">
          <div class="booking-status" style="color: ${statusColor};">
            <span class="status-icon">${statusIcon}</span>
            <span class="status-text">${statusText}</span>
          </div>
          <div class="booking-date">${formatDate(booking.createdAt)}</div>
        </div>
        
        <div class="booking-confirmation">
          <div class="label">${t('confirmationNumber')}</div>
          <div class="value" style="color: ${statusColor}; font-weight: bold; font-size: 18px;">
            ${booking.confirmationNumber}
          </div>
        </div>
        
        <div class="booking-details">
          ${(() => {
            const lang = (getCurrentLanguage() || 'th') as Language;
            const isRfp = booking.bookingType === 'RFP' ||
              (booking as any).engine === 'request-rfp' ||
              (booking as any).engine === 'request_rfp' ||
              (booking as any).serviceId === 'member_services' ||
              (booking as any).customFields?.bookingType === 'RFP' ||
              (booking as any).customFields?.engine === 'request-rfp' ||
              (booking as any).customFields?.engine === 'request_rfp' ||
              (booking as any).customFields?.serviceId === 'member_services';
            const isSeminar = (booking.bookingType === 'COURSE' ||
              (booking as any).resourceType === 'course' ||
              (booking as any).resourceType === 'class' ||
              (booking as any).resourceType === 'workshop' ||
              String(booking.resourceName || '').includes('CPD') ||
              String(booking.roomTypeName || '').includes('CPD') ||
              String((booking as any).itemName || '').includes('CPD') ||
              String(booking.resourceName || '').includes('สัมมนา') ||
              String(booking.roomTypeName || '').includes('สัมมนา') ||
              String((booking as any).itemName || '').includes('สัมมนา') ||
              String(booking.resourceName || '').includes('อบรม') ||
              String(booking.roomTypeName || '').includes('อบรม') ||
              String((booking as any).itemName || '').includes('อบรม')) && !isRfp;
            const isSpa = (booking.bookingType === 'SPA' || (Boolean((booking as any).spaDetails) && !(booking as any).engine && (booking as any).bookingType !== 'APPOINTMENT')) && !isRfp && !isSeminar;
            const isRestaurant = (booking.bookingType === 'RESTAURANT' || Boolean((booking as any).restaurantDetails)) && !isRfp && !isSeminar;
            const isAppointment = ((booking.bookingType === 'APPOINTMENT' || (booking as any).engine === 'appointment') && !isRfp && !isSeminar);
            const isMice = (booking.bookingType === 'MICE' ||
              booking.bookingType === 'EVENT' ||
              (booking as any).resourceType === 'facility' ||
              (booking as any).customFields?.bookingType === 'MICE' ||
              String(booking.confirmationNumber || '').startsWith('MICE-') ||
              String(booking.id || '').startsWith('MICE-') ||
              Boolean((booking as any).miceDetails) ||
              Boolean((booking as any).customFields?.miceDetails)) && !isRfp && !isSeminar && !isAppointment;
            const guestFullName = booking.guestFirstName && booking.guestLastName
              ? `${booking.guestFirstName} ${booking.guestLastName}`
              : ((booking as any).guestName || '-');

            // Payment display calculation
            let paymentDisplay: string | null = null;
            if (isRfp) {
              paymentDisplay = lang === 'th' ? 'ขอใบเสนอราคา (Quotation on Request)' : 'Quotation on Request';
            } else if (isSeminar) {
              paymentDisplay = booking.paymentStatus === 'paid' ? t('statusPaid') : (lang === 'th' ? 'ชำระ ณ จุดลงทะเบียน (Pay at Venue)' : 'Pay at Venue');
            } else if (isMice) {
              if (booking.paymentStatus === 'paid') {
                paymentDisplay = t('statusPaid');
              } else if (booking.totalAmount > 0) {
                paymentDisplay = MICE_PAY_AT_VENUE[lang] || MICE_PAY_AT_VENUE.en;
              } else {
                paymentDisplay = MICE_RFP[lang] || MICE_RFP.en;
              }
            } else if (booking.paymentCardBrand && booking.paymentCardLast4) {
              const brand = booking.paymentCardBrand.charAt(0).toUpperCase() + booking.paymentCardBrand.slice(1);
              paymentDisplay = `${brand} ****${booking.paymentCardLast4}`;
            } else if (booking.paymentMethod && booking.paymentMethod.includes('_')) {
              const parts = booking.paymentMethod.split('_');
              if (parts.length === 2 && parts[1].length === 4) {
                const brand = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
                paymentDisplay = `${brand} ****${parts[1]}`;
              } else {
                paymentDisplay = booking.paymentStatus === 'paid' ? t('statusPaid') : (isSpa ? (PAY_AT_SPA[lang] || PAY_AT_SPA.en) : isRestaurant ? (PAY_AT_RESTAURANT[lang] || PAY_AT_RESTAURANT.en) : t('skipPayment'));
              }
            } else if (booking.paymentMethod === 'promptpay') {
              paymentDisplay = 'PromptPay';
            } else if (booking.paymentStatus === 'paid') {
              paymentDisplay = t('statusPaid');
            } else if (!booking.paymentStatus || booking.paymentStatus === 'pay_at_hotel') {
              paymentDisplay = isSpa
                ? (PAY_AT_SPA[lang] || PAY_AT_SPA.en)
                : isRestaurant
                  ? (PAY_AT_RESTAURANT[lang] || PAY_AT_RESTAURANT.en)
                  : t('skipPayment');
            }

            if (isSpa) {
              const spa = (booking as any).spaDetails || {};
              const spaOutletName = typeof spa.outletName === 'object'
                ? (spa.outletName[lang] || spa.outletName.en || spa.outletName.th || Object.values(spa.outletName)[0] || LABEL_SPA[lang] || 'Spa')
                : (spa.outletName || LABEL_SPA[lang] || 'Spa');
              const treatmentTitle = typeof spa.treatmentName === 'object'
                ? (spa.treatmentName[lang] || spa.treatmentName.en || spa.treatmentName.th || Object.values(spa.treatmentName)[0] || LABEL_TREATMENT[lang] || 'Treatment')
                : (spa.treatmentName || booking.roomTypeName || LABEL_TREATMENT[lang] || 'Treatment');
              const serviceDate = spa.bookingDate || booking.checkIn;
              const timeSlotStr = spa.timeSlot ? `${spa.timeSlot}${lang === 'th' ? ' น.' : ''}${spa.durationMinutes ? ` (${spa.durationMinutes} ${DURATION_MINS[lang] || 'mins'})` : ''}` : '-';
              const paxCount = spa.pax || booking.adults || 1;

              return `
                <div class="detail-row">
                  <span class="detail-label">🌿 ${LABEL_SPA[lang] || 'Spa'}</span>
                  <span class="detail-value" style="font-weight:bold; color:#7e22ce;">${spaOutletName}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">💆 ${LABEL_TREATMENT[lang] || 'Treatment'}</span>
                  <span class="detail-value">${treatmentTitle}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">👤 ${t('guestName')}</span>
                  <span class="detail-value">${guestFullName}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">📅 ${LABEL_SERVICE_DATE[lang] || 'Service Date'}</span>
                  <span class="detail-value">${formatDate(serviceDate)}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">⏰ ${LABEL_TIME_SLOT[lang] || 'Time Slot'}</span>
                  <span class="detail-value">${timeSlotStr}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">👥 ${t('guests')}</span>
                  <span class="detail-value">${paxCount} ${t('adults')}</span>
                </div>
                ${spa.roomNumber ? `
                <div class="detail-row">
                  <span class="detail-label">🚪 ${LABEL_ROOM_NUMBER[lang] || 'Room Number'}</span>
                  <span class="detail-value">${formatRoomNumber(spa.roomNumber, lang)}</span>
                </div>` : ''}
                ${booking.guestEmail ? `
                <div class="detail-row">
                  <span class="detail-label">📧 ${t('email')}</span>
                  <span class="detail-value" style="font-size: 12px;">${booking.guestEmail}</span>
                </div>` : ''}
                ${paymentDisplay ? `
                <div class="detail-row" style="margin-top: 4px;">
                  <span class="detail-label">💳 ${t('paymentTitle')}</span>
                  <span class="detail-value" style="color: ${booking.paymentStatus === 'paid' ? '#00B900' : '#0284c7'}; font-weight: 600;">${paymentDisplay}</span>
                </div>` : ''}

                ${
                  booking.totalAmount > 0
                    ? (() => {
                        const extraChargeTotal = booking.extraCharges?.totalExtraCharge || 0;
                        const spaRateBeforeTax = (booking.totalBeforeTax && booking.totalBeforeTax < booking.totalAmount)
                          ? booking.totalBeforeTax
                          : Math.round((booking.totalAmount - extraChargeTotal) / 1.177);
                        const svc = Math.round(spaRateBeforeTax * 0.10);
                        const vat = Math.round((spaRateBeforeTax + svc) * 0.07);

                        return `
                <!-- Price Breakdown -->
                <div style="margin-top: 12px; padding-top: 12px; border-top: 1px dashed #e5e5e5;">
                  <div class="detail-row" style="font-size: 13px; color: #888;">
                    <span class="detail-label">💵 ${t('serviceRateBeforeTax')}</span>
                    <span class="detail-value">${formatCurrency(spaRateBeforeTax, booking.currency)}</span>
                  </div>
                  ${
                    extraChargeTotal > 0
                      ? `
                  <div class="detail-row" style="font-size: 13px; color: #f59e0b;">
                    <span class="detail-label">➕ ${t('extraCharges')}</span>
                    <span class="detail-value">${formatCurrency(extraChargeTotal, booking.currency)}</span>
                  </div>
                  `
                      : ''
                  }
                  <div class="detail-row" style="font-size: 13px; color: #888;">
                    <span class="detail-label">📊 ${t('serviceCharge10')}</span>
                    <span class="detail-value">${formatCurrency(svc, booking.currency)}</span>
                  </div>
                  <div class="detail-row" style="font-size: 13px; color: #888;">
                    <span class="detail-label">📊 ${t('vat7')}</span>
                    <span class="detail-value">${formatCurrency(vat, booking.currency)}</span>
                  </div>
                </div>`;
                      })()
                    : ''
                }

                <div class="detail-row total-row" style="background: #f8f9fa; margin: 8px -16px -16px; padding: 12px 16px; border-radius: 0 0 8px 8px;">
                  <span class="detail-label" style="font-size: 16px; font-weight: bold;">💰 ${t('totalAmount')}</span>
                  <span class="detail-value" style="color: ${statusColor}; font-weight: bold; font-size: 18px;">
                    ${formatCurrency(booking.totalAmount, booking.currency)}
                  </span>
                </div>
              `;
            }

            if (isRestaurant) {
              const rest = (booking as any).restaurantDetails || {};
              const restOutletName = typeof rest.outletName === 'object'
                ? (rest.outletName[lang] || rest.outletName.en || rest.outletName.th || Object.values(rest.outletName)[0] || LABEL_RESTAURANT[lang] || 'Restaurant')
                : (rest.outletName || LABEL_RESTAURANT[lang] || 'Restaurant');
              const serviceDate = rest.bookingDate || booking.checkIn;
              const timeSlotStr = rest.timeSlot ? `${rest.timeSlot}${lang === 'th' ? ' น.' : ''}` : '-';
              const partySize = rest.partySize || booking.adults || 2;
              const seatingArea = resolveSeatingArea(rest.seatingArea, lang);

              return `
                <div class="detail-row">
                  <span class="detail-label">🍽️ ${LABEL_RESTAURANT[lang] || 'Restaurant'}</span>
                  <span class="detail-value" style="font-weight:bold; color:#ea580c;">${restOutletName}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">🪑 ${LABEL_SEATING_AREA[lang] || 'Seating Area'}</span>
                  <span class="detail-value">${seatingArea}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">👤 ${t('guestName')}</span>
                  <span class="detail-value">${guestFullName}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">📅 ${LABEL_SERVICE_DATE[lang] || 'Service Date'}</span>
                  <span class="detail-value">${formatDate(serviceDate)}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">⏰ ${LABEL_TIME_SLOT[lang] || 'Time Slot'}</span>
                  <span class="detail-value">${timeSlotStr}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">👥 ${LABEL_PARTY_SIZE[lang] || 'Party Size'}</span>
                  <span class="detail-value">${partySize} ${PARTY_UNIT[lang] || 'Guests'}</span>
                </div>
                ${rest.roomNumber ? `
                <div class="detail-row">
                  <span class="detail-label">🚪 ${LABEL_ROOM_NUMBER[lang] || 'Room Number'}</span>
                  <span class="detail-value">${formatRoomNumber(rest.roomNumber, lang)}</span>
                </div>` : ''}
                ${booking.guestEmail ? `
                <div class="detail-row">
                  <span class="detail-label">📧 ${t('email')}</span>
                  <span class="detail-value" style="font-size: 12px;">${booking.guestEmail}</span>
                </div>` : ''}
                ${paymentDisplay ? `
                <div class="detail-row" style="margin-top: 4px;">
                  <span class="detail-label">💳 ${t('paymentTitle')}</span>
                  <span class="detail-value" style="color: ${booking.paymentStatus === 'paid' ? '#00B900' : '#0284c7'}; font-weight: 600;">${paymentDisplay}</span>
                </div>` : ''}
                ${booking.totalAmount > 0 ? `
                <div class="detail-row total-row" style="background: #f8f9fa; margin: 8px -16px -16px; padding: 12px 16px; border-radius: 0 0 8px 8px;">
                  <span class="detail-label" style="font-size: 16px; font-weight: bold;">💰 ${t('totalAmount')}</span>
                  <span class="detail-value" style="color: ${statusColor}; font-weight: bold; font-size: 18px;">
                    ${formatCurrency(booking.totalAmount, booking.currency)}
                  </span>
                </div>` : ''}
              `;
            }

            if (isMice) {
              const mice = (booking as any).miceDetails || (booking as any).customFields?.miceDetails || {};
              const roomName = typeof mice.functionRoomName === 'object'
                ? (mice.functionRoomName[lang] || mice.functionRoomName.en || mice.functionRoomName.th || Object.values(mice.functionRoomName)[0])
                : (mice.functionRoomName || booking.roomTypeName || MICE_VENUE[lang] || 'Function Room');
              const layout = typeof mice.layoutStyle === 'string'
                ? (mice.layoutStyle.charAt(0).toUpperCase() + mice.layoutStyle.slice(1))
                : 'Classroom';
              const pkgName = typeof mice.packageName === 'object'
                ? (mice.packageName[lang] || mice.packageName.en || mice.packageName.th || Object.values(mice.packageName)[0])
                : (mice.packageName || (booking.ratePlanName && booking.ratePlanName !== 'Custom Quotation' && booking.ratePlanName !== 'Standard MICE Plan' ? booking.ratePlanName : ''));
              const company = mice.companyName || (booking as any).companyName || (booking as any).customFields?.companyName;
              const eventDate = mice.eventDate || booking.checkIn;
              const timeSlot = mice.timeSlotName || mice.timeSlot || '08:30 - 17:00 (Full-Day)';
              const pax = mice.expectedPax || mice.pax || booking.adults || 20;

              return `
                <div class="detail-row">
                  <span class="detail-label">🏛️ ${MICE_VENUE[lang] || 'Function Room'}</span>
                  <span class="detail-value" style="font-weight:bold; color:#1e40af;">${roomName}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">🪑 ${MICE_LAYOUT[lang] || 'Seating Layout'}</span>
                  <span class="detail-value">${layout}</span>
                </div>
                ${pkgName ? `
                <div class="detail-row">
                  <span class="detail-label">📦 ${MICE_PACKAGE[lang] || 'Package'}</span>
                  <span class="detail-value">${pkgName}</span>
                </div>` : ''}
                ${company ? `
                <div class="detail-row">
                  <span class="detail-label">🏢 ${MICE_COMPANY[lang] || 'Company'}</span>
                  <span class="detail-value">${company}</span>
                </div>` : ''}
                <div class="detail-row">
                  <span class="detail-label">👤 ${MICE_ORGANIZER[lang] || 'Organizer'}</span>
                  <span class="detail-value">${guestFullName}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">📅 ${MICE_EVENT_DATE[lang] || 'Event Date'}</span>
                  <span class="detail-value">${formatDate(eventDate)}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">⏰ ${MICE_TIME_SLOT[lang] || 'Time Slot'}</span>
                  <span class="detail-value">${timeSlot}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">👥 ${MICE_ATTENDEES[lang] || 'Attendees'}</span>
                  <span class="detail-value">${formatMicePax(pax, lang)}</span>
                </div>
                ${booking.guestEmail ? `
                <div class="detail-row">
                  <span class="detail-label">📧 ${t('email')}</span>
                  <span class="detail-value" style="font-size: 12px;">${booking.guestEmail}</span>
                </div>` : ''}
                ${(booking as any).guestPhone || (booking as any).phone ? `
                <div class="detail-row">
                  <span class="detail-label">📞 ${t('phone')}</span>
                  <span class="detail-value">${(booking as any).guestPhone || (booking as any).phone}</span>
                </div>` : ''}
                ${booking.specialRequests ? `
                <div class="detail-row" style="flex-direction: column; align-items: flex-start;">
                  <span class="detail-label">📝 ${t('specialRequests')}</span>
                  <span class="detail-value" style="margin-top: 4px; font-size: 13px; color: #666; font-style: italic;">${booking.specialRequests}</span>
                </div>` : ''}
                ${paymentDisplay ? `
                <div class="detail-row" style="margin-top: 4px;">
                  <span class="detail-label">💳 ${t('paymentTitle')}</span>
                  <span class="detail-value" style="color: ${booking.paymentStatus === 'paid' ? '#00B900' : '#1e40af'}; font-weight: 600;">${paymentDisplay}</span>
                </div>` : ''}

                ${booking.totalAmount > 0 ? `
                <div class="detail-row total-row" style="background: #f8f9fa; margin: 8px -16px -16px; padding: 12px 16px; border-radius: 0 0 8px 8px;">
                  <span class="detail-label" style="font-size: 16px; font-weight: bold;">💰 ${t('totalAmount')}</span>
                  <span class="detail-value" style="color: ${statusColor}; font-weight: bold; font-size: 18px;">
                    ${formatCurrency(booking.totalAmount, booking.currency)}
                  </span>
                </div>` : `
                <div class="detail-row total-row" style="background: #eff6ff; margin: 8px -16px -16px; padding: 12px 16px; border-radius: 0 0 8px 8px; border-top: 1px solid #bfdbfe;">
                  <span class="detail-label" style="font-size: 14px; font-weight: bold; color: #1e40af;">📄 ${MICE_RFP_STATUS_LABEL[lang] || 'Quotation Status'}</span>
                  <span class="detail-value" style="color: #1e40af; font-weight: bold; font-size: 14px;">
                    ${MICE_RFP_STATUS_TEXT[lang] || 'Quotation Pending (RFP)'}
                  </span>
                </div>
                <div style="margin-top: 12px; padding: 10px; background: #eff6ff; border-left: 3px solid #3b82f6; border-radius: 4px; font-size: 12px; color: #1e40af;">
                  💬 ${MICE_RFP_NOTICE[lang] || 'Our event specialist will contact you shortly to coordinate event details.'}
                </div>`}
              `;
            }

            if (isSeminar) {
              const appt = (booking as any).spaDetails || {};
              const resName = booking.resourceName || (typeof appt.outletName === 'object' ? (appt.outletName[lang] || appt.outletName.en || appt.outletName.th) : appt.outletName) || '-';
              const treatTitle = (booking as any).itemName || (booking as any).courseName || booking.roomTypeName || (typeof appt.treatmentName === 'object' ? (appt.treatmentName[lang] || appt.treatmentName.en || appt.treatmentName.th) : appt.treatmentName) || '-';
              const serviceDate = appt.bookingDate || booking.checkIn;
              const timeSlotStr = appt.timeSlot ? `${appt.timeSlot}${lang === 'th' ? ' น.' : ''}` : '-';
              const seats = booking.adults || (booking as any).pax || 1;

              return `
                <div class="detail-row">
                  <span class="detail-label">🎓 ${lang === 'th' ? 'หลักสูตร / หัวข้ออบรม' : 'Course / Topic'}</span>
                  <span class="detail-value" style="font-weight:bold; color:#0284c7;">${treatTitle}</span>
                </div>
                ${resName && resName !== '-' ? `
                <div class="detail-row">
                  <span class="detail-label">📍 ${lang === 'th' ? 'สมาคม / ผู้จัด' : 'Organizer'}</span>
                  <span class="detail-value">${resName}</span>
                </div>` : ''}
                <div class="detail-row">
                  <span class="detail-label">👤 ${t('guestName')}</span>
                  <span class="detail-value">${guestFullName}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">📅 ${LABEL_SERVICE_DATE[lang] || 'Service Date'}</span>
                  <span class="detail-value">${formatDate(serviceDate)}</span>
                </div>
                ${timeSlotStr && timeSlotStr !== '-' ? `
                <div class="detail-row">
                  <span class="detail-label">⏰ ${LABEL_TIME_SLOT[lang] || 'Time Slot'}</span>
                  <span class="detail-value">${timeSlotStr}</span>
                </div>` : ''}
                <div class="detail-row">
                  <span class="detail-label">👥 ${lang === 'th' ? 'จำนวนที่นั่ง' : 'Seats'}</span>
                  <span class="detail-value">${seats} ${lang === 'th' ? 'ที่นั่ง' : 'Seat(s)'}</span>
                </div>
                ${paymentDisplay ? `
                <div class="detail-row" style="margin-top: 4px;">
                  <span class="detail-label">💳 ${t('paymentTitle')}</span>
                  <span class="detail-value" style="color: ${booking.paymentStatus === 'paid' ? '#00B900' : '#0284c7'}; font-weight: 600;">${paymentDisplay}</span>
                </div>` : ''}

                <div class="detail-row total-row" style="background: #f8f9fa; margin: 8px -16px -16px; padding: 12px 16px; border-radius: 0 0 8px 8px;">
                  <span class="detail-label" style="font-size: 16px; font-weight: bold;">💰 ${t('totalAmount')}</span>
                  <span class="detail-value" style="color: ${statusColor}; font-weight: bold; font-size: 18px;">
                    ${formatCurrency(booking.totalAmount, booking.currency)}
                  </span>
                </div>
              `;
            }

            if (isAppointment) {
              const appt = (booking as any).spaDetails || {};
              const resName = booking.resourceName || (typeof appt.outletName === 'object' ? (appt.outletName[lang] || appt.outletName.en || appt.outletName.th) : appt.outletName) || '-';
              const treatTitle = booking.roomTypeName || (typeof appt.treatmentName === 'object' ? (appt.treatmentName[lang] || appt.treatmentName.en || appt.treatmentName.th) : appt.treatmentName) || '-';
              const serviceDate = appt.bookingDate || booking.checkIn;
              const timeSlotStr = appt.timeSlot ? `${appt.timeSlot}${lang === 'th' ? ' น.' : ''}` : '-';

              return `
                <div class="detail-row">
                  <span class="detail-label">🩺 ${lang === 'th' ? 'บริการ' : 'Service'}</span>
                  <span class="detail-value" style="font-weight:bold; color:#0284c7;">${treatTitle}</span>
                </div>
                ${resName && resName !== '-' ? `
                <div class="detail-row">
                  <span class="detail-label">📍 ${lang === 'th' ? 'สถานที่ / สาขา' : 'Branch / Location'}</span>
                  <span class="detail-value">${resName}</span>
                </div>` : ''}
                <div class="detail-row">
                  <span class="detail-label">👤 ${t('guestName')}</span>
                  <span class="detail-value">${guestFullName}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">📅 ${LABEL_SERVICE_DATE[lang] || 'Service Date'}</span>
                  <span class="detail-value">${formatDate(serviceDate)}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-label">⏰ ${LABEL_TIME_SLOT[lang] || 'Time Slot'}</span>
                  <span class="detail-value">${timeSlotStr}</span>
                </div>
                ${paymentDisplay ? `
                <div class="detail-row" style="margin-top: 4px;">
                  <span class="detail-label">💳 ${t('paymentTitle')}</span>
                  <span class="detail-value" style="color: ${booking.paymentStatus === 'paid' ? '#00B900' : '#0284c7'}; font-weight: 600;">${paymentDisplay}</span>
                </div>` : ''}

                <div class="detail-row total-row" style="background: #f8f9fa; margin: 8px -16px -16px; padding: 12px 16px; border-radius: 0 0 8px 8px;">
                  <span class="detail-label" style="font-size: 16px; font-weight: bold;">💰 ${t('totalAmount')}</span>
                  <span class="detail-value" style="color: ${statusColor}; font-weight: bold; font-size: 18px;">
                    ${formatCurrency(booking.totalAmount, booking.currency)}
                  </span>
                </div>
              `;
            }

            if (isRfp) {
              const rfp = (booking as any).miceDetails || (booking as any).customFields?.miceDetails || {};
              const resName = booking.resourceName || (booking as any).itemName || (typeof rfp.functionRoomName === 'object' ? (rfp.functionRoomName[lang] || rfp.functionRoomName.en || rfp.functionRoomName.th) : rfp.functionRoomName) || booking.roomTypeName || (lang === 'th' ? 'บริการสมาชิกและยื่นคำขอ' : 'Member Services');
              const eventDate = rfp.eventDate || booking.checkIn;
              const reqDetails = (booking as any).customFields?.specialRequests || (booking as any).customFields?.requirements || rfp.specialRequests || (booking as any).specialRequests || '';

              return `
                <div class="detail-row">
                  <span class="detail-label">📋 ${lang === 'th' ? 'ประเภทคำขอ / บริการ' : 'Request Type / Service'}</span>
                  <span class="detail-value" style="font-weight:bold; color:#1e40af;">${resName}</span>
                </div>
                ${reqDetails ? `
                <div class="detail-row" style="background: #f8fafc; padding: 8px 10px; border-radius: 6px; margin: 6px 0; border: 1px solid #e2e8f0;">
                  <span class="detail-label" style="font-size: 12px; font-weight: 600; color: #475569;">📝 ${lang === 'th' ? 'รายละเอียดความต้องการ / โครงการ' : 'Requirements'}</span>
                  <span class="detail-value" style="color: #0f172a; font-weight: 500; font-size: 13px;">${reqDetails}</span>
                </div>` : ''}
                <div class="detail-row">
                  <span class="detail-label">👤 ${t('guestName')}</span>
                  <span class="detail-value">${guestFullName}</span>
                </div>
                ${eventDate ? `
                <div class="detail-row">
                  <span class="detail-label">📅 ${lang === 'th' ? 'วันที่ต้องการ' : 'Requested Date'}</span>
                  <span class="detail-value">${formatDate(eventDate)}</span>
                </div>` : ''}
                ${rfp.expectedPax ? `
                <div class="detail-row">
                  <span class="detail-label">👥 ${lang === 'th' ? 'จำนวน' : 'Quantity / Pax'}</span>
                  <span class="detail-value">${rfp.expectedPax}</span>
                </div>` : ''}
                <div class="detail-row total-row" style="background: #eff6ff; margin: 8px -16px -16px; padding: 12px 16px; border-radius: 0 0 8px 8px; border-top: 1px solid #bfdbfe;">
                  <span class="detail-label" style="font-size: 14px; font-weight: bold; color: #1e40af;">📄 ${MICE_RFP_STATUS_LABEL[lang] || 'Quotation Status'}</span>
                  <span class="detail-value" style="color: #1e40af; font-weight: bold; font-size: 14px;">
                    ${MICE_RFP_STATUS_TEXT[lang] || 'Quotation Pending (RFP)'}
                  </span>
                </div>
              `;
            }

            // Standard Room Booking
            return `
              <div class="detail-row">
                <span class="detail-label">🏨 ${t('roomType')}</span>
                <span class="detail-value">${booking.roomTypeName || ''}</span>
              </div>
              ${
                booking.ratePlanName
                  ? `
              <div class="detail-row">
                <span class="detail-label">📋 ${t('ratePlan')}</span>
                <span class="detail-value">${booking.ratePlanName}${booking.isRefundable === false ? ` <span style="display:inline-block; background:#dc2626; color:#fff; font-size:11px; font-weight:600; padding:1px 7px; border-radius:4px; margin-left:4px;">${t('nonRefundable')}</span>` : ''}</span>
              </div>
              `
                  : ''
              }
              <div class="detail-row">
                <span class="detail-label">📅 ${t('checkIn')}</span>
                <span class="detail-value">${formatDate(booking.checkIn)}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">📅 ${t('checkOut')}</span>
                <span class="detail-value">${formatDate(booking.checkOut)}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">🌙 ${t('nights')}</span>
                <span class="detail-value">${booking.nights || '-'}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">🚪 ${t('rooms')}</span>
                <span class="detail-value">${booking.rooms}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">👥 ${t('guests')}</span>
                <span class="detail-value">${booking.adults} ${t('adults')}${booking.children > 0 ? `, ${booking.children} ${t('children')}` : ''}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">👤 ${t('guestName')}</span>
                <span class="detail-value">${guestFullName}</span>
              </div>
              ${
                booking.guestEmail
                  ? `
              <div class="detail-row">
                <span class="detail-label">📧 ${t('email')}</span>
                <span class="detail-value" style="font-size: 12px;">${booking.guestEmail}</span>
              </div>
              `
                  : ''
              }
              ${
                booking.specialRequests
                  ? `
              <div class="detail-row" style="flex-direction: column; align-items: flex-start;">
                <span class="detail-label">📝 ${t('specialRequests')}</span>
                <span class="detail-value" style="margin-top: 4px; font-size: 13px; color: #666; font-style: italic;">${booking.specialRequests}</span>
              </div>
              `
                  : ''
              }
              
              <!-- Price Breakdown -->
              <div style="margin-top: 12px; padding-top: 12px; border-top: 1px dashed #e5e5e5;">
                ${
                  booking.ratePerRoom
                    ? `
                <div class="detail-row">
                  <span class="detail-label">🛏️ ${t('roomRate')}${booking.rooms > 1 ? ` (${booking.rooms} ${t('rooms')})` : ''}</span>
                  <span class="detail-value">${booking.rooms > 1 ? `${booking.rooms} × ` : ''}${formatCurrency(booking.ratePerRoom, booking.currency)}</span>
                </div>
                `
                    : ''
                }
                ${
                  booking.extraCharges && booking.extraCharges.totalExtraCharge > 0
                    ? `
                <div class="detail-row" style="color: #f59e0b;">
                  <span class="detail-label">➕ ${t('extraCharges')}</span>
                  <span class="detail-value">${formatCurrency(booking.extraCharges.totalExtraCharge, booking.currency)}</span>
                </div>
                <div style="font-size: 11px; color: #888; text-align: right; margin-top: -4px; margin-bottom: 8px;">
                  ${booking.extraCharges.extraAdults > 0 ? `${booking.extraCharges.extraAdults} ${t('extraAdults')}` : ''}
                  ${booking.extraCharges.extraAdults > 0 && booking.extraCharges.extraChildren > 0 ? ', ' : ''}
                  ${booking.extraCharges.extraChildren > 0 ? `${booking.extraCharges.extraChildren} ${t('extraChildren')}` : ''}
                </div>
                `
                    : ''
                }
                ${
                  booking.totalAmount > 0
                    ? (() => {
                        const extraChargeTotal = booking.extraCharges?.totalExtraCharge || 0;
                        const roomRateBeforeTax = (booking.totalBeforeTax && booking.totalBeforeTax < booking.totalAmount)
                          ? booking.totalBeforeTax
                          : Math.round((booking.totalAmount - extraChargeTotal) / 1.177);
                        const svc = Math.round(roomRateBeforeTax * 0.10);
                        const vat = Math.round((roomRateBeforeTax + svc) * 0.07);
                        return `
                <div style="border-top: 1px dashed #e5e5e5; margin: 8px 0; padding-top: 8px;">
                  <div class="detail-row" style="font-size: 13px; color: #888;">
                    <span class="detail-label">💵 ${t('roomRateBeforeTax')}</span>
                    <span class="detail-value">${formatCurrency(roomRateBeforeTax, booking.currency)}</span>
                  </div>
                  <div class="detail-row" style="font-size: 13px; color: #888;">
                    <span class="detail-label">📊 ${t('serviceCharge10')}</span>
                    <span class="detail-value">${formatCurrency(svc, booking.currency)}</span>
                  </div>
                  <div class="detail-row" style="font-size: 13px; color: #888;">
                    <span class="detail-label">📊 ${t('vat7')}</span>
                    <span class="detail-value">${formatCurrency(vat, booking.currency)}</span>
                  </div>
                </div>`;
                      })()
                    : ''
                }
              </div>
              
              ${paymentDisplay ? `
              <div class="detail-row" style="margin-top: 4px;">
                <span class="detail-label">💳 ${t('paymentTitle')}</span>
                <span class="detail-value" style="color: ${booking.paymentStatus === 'paid' ? '#00B900' : '#666'}; font-weight: ${booking.paymentStatus === 'paid' ? 'bold' : 'normal'};">${paymentDisplay}</span>
              </div>` : ''}

              <div class="detail-row total-row" style="background: #f8f9fa; margin: 8px -16px -16px; padding: 12px 16px; border-radius: 0 0 8px 8px;">
                <span class="detail-label" style="font-size: 16px; font-weight: bold;">💰 ${t('totalAmount')}</span>
                <span class="detail-value" style="color: ${statusColor}; font-weight: bold; font-size: 18px;">
                  ${formatCurrency(booking.totalAmount, booking.currency)}
                </span>
              </div>
            `;
          })()}
        </div>
        
        ${
          booking.status === 'confirmed' && booking.paymentStatus === 'paid'
            ? `
          <div style="margin-top: 12px; padding: 10px; background: #e8f5e9; border-left: 3px solid #00B900; border-radius: 4px; font-size: 12px; color: #1b5e20;">
            💳 ${t('paidBookingCannotModify')}
          </div>
        `
            : booking.isRefundable === false
              ? `
          <div style="margin-top: 12px; padding: 10px; background: #fff3cd; border-left: 3px solid #ffc107; border-radius: 4px; font-size: 12px; color: #856404;">
            ⛔ ${t('nonRefundableCannotModify')}
          </div>
        `
            : booking.status === 'confirmed' && canModifyBooking(booking.checkIn, deadlineDays)
              ? `
          <div class="booking-actions" style="display: flex; gap: 8px; margin-top: 12px;">
            <button 
              type="button"
              class="btn btn-outline"
              onclick="window.editBooking('${booking.id}')"
              style="flex: 1; padding: 12px; font-size: 14px;"
            >
              ✏️ ${t('editBooking')}
            </button>
            <button 
              type="button"
              class="btn btn-outline-danger"
              onclick="window.cancelBooking('${booking.id}', '${booking.confirmationNumber}')"
              style="flex: 1; padding: 12px; font-size: 14px;"
            >
              ❌ ${t('cancelBooking')}
            </button>
          </div>
        `
              : booking.status === 'confirmed'
                ? `
          <div style="margin-top: 12px; padding: 10px; background: #fff3cd; border-left: 3px solid #ffc107; border-radius: 4px; font-size: 12px; color: #856404;">
            ℹ️ ${t('cannotModifyBooking')}
          </div>
        `
                : ''
        }
      </div>
    `,
        'booking-item-card'
      );
    })
    .join('');

  container.innerHTML = `
    <div class="bookings-header">
      <h2>${t('myBookings')}</h2>
      <p class="bookings-count">${bookings.length} ${t('bookings')}</p>
    </div>
    ${bookingsHtml}
    <div style="padding: 20px 0; text-align: center;">
      <button 
        type="button" 
        class="btn btn-orange btn-block" 
        onclick="window.closeMyBookings()"
        style="max-width: 400px; margin: 0 auto;"
      >
        ${t('close')}
      </button>
    </div>
    ${AppVersionFooter()}
  `;

  window.closeMyBookings = () => {
    closeWindow();
  };

  // Initialize edit/cancel handlers
  initBookingHandlers();
}

/**
 * Initialize booking edit/cancel handlers
 */
function initBookingHandlers(): void {
  window.editBooking = async (bookingId: string) => {
    console.log('[MyBookings] Edit booking:', bookingId);
    try {
      const { getTenantId } = await import('../config');
      const { apiFetch } = await import('../api');
      const { navigateTo } = await import('../store');
      const tenantId = getTenantId();

      const result = await apiFetch<{ success: boolean; data: any }>(
        `/api/v1/${tenantId}/bookings/${bookingId}`
      );

      if (!result.success || !result.data) {
        alert(t('errorLoadingBooking'));
        return;
      }

      const booking = result.data;
      sessionStorage.setItem('editBookingId', bookingId);
      sessionStorage.setItem('editBookingData', JSON.stringify(booking));

      const type = (booking.bookingType || (booking.spaDetails ? 'SPA' : booking.restaurantDetails ? 'RESTAURANT' : (booking.miceDetails || String(booking.confirmationNumber || '').startsWith('MICE-') ? 'MICE' : 'ROOM'))).toUpperCase();

      if (type === 'SPA') {
        navigateTo('spa');
      } else if (type === 'RESTAURANT') {
        navigateTo('restaurant');
      } else if (type === 'MICE') {
        navigateTo('mice');
      } else {
        navigateTo('search');
      }
    } catch (e) {
      console.error('[MyBookings] Failed to start editing booking:', e);
      alert(t('errorNetwork'));
    }
  };

  window.cancelBooking = async (bookingId: string, confirmationNumber: string) => {
    console.log('[MyBookings] Cancel booking:', bookingId);
    showCancelConfirmationModal(bookingId, confirmationNumber);
  };
}

/**
 * Show cancel confirmation modal
 */
function showCancelConfirmationModal(bookingId: string, confirmationNumber: string): void {
  const modalHtml = `
    <div id="cancel-booking-modal" class="modal-overlay" style="display: flex;">
      <div class="modal-content" style="max-width: 450px;">
        <div class="modal-header" style="background: #fff3cd; border-bottom: 2px solid #ffc107;">
          <h3 style="color: #856404;">⚠️ ${t('cancelBooking')}</h3>
          <button type="button" class="modal-close" onclick="window.closeCancelModal()">&times;</button>
        </div>
        
        <div class="modal-body">
          <p style="font-size: 16px; margin-bottom: 16px;">
            ${t('confirmCancelBooking')}
          </p>
          
          <div style="background: #f8f9fa; padding: 12px; border-radius: 8px; margin-bottom: 16px;">
            <strong>${t('confirmationNumber')}:</strong><br/>
            <span style="font-size: 18px; color: #dc3545;">${confirmationNumber}</span>
          </div>
          
          <div style="background: #fff3cd; padding: 12px; border-left: 3px solid #ffc107; border-radius: 4px; margin-bottom: 16px;">
            <strong style="color: #856404;">📋 ${t('cancellationPolicy')}</strong>
            <ul style="margin: 8px 0 0 0; padding-left: 20px; color: #856404; font-size: 14px;">
              <li>${t('cancellationPolicy3Days')}</li>
              <li>${t('bookingWillBeCancelled')}</li>
            </ul>
          </div>
          
          <p style="color: #dc3545; font-weight: bold; text-align: center;">
            ${t('actionCannotBeUndone')}
          </p>
        </div>
        
        <div class="modal-footer">
          <button type="button" class="btn btn-outline" onclick="window.closeCancelModal()">
            ${t('keepBooking')}
          </button>
          <button type="button" class="btn btn-danger" onclick="window.confirmCancelBooking('${bookingId}')">
            ❌ ${t('confirmCancel')}
          </button>
        </div>
      </div>
    </div>
  `;

  document.body.insertAdjacentHTML('beforeend', modalHtml);

  // Close modal on overlay click
  const modal = document.getElementById('cancel-booking-modal');
  modal?.addEventListener('click', e => {
    if (e.target === modal) {
      window.closeCancelModal();
    }
  });

  // Close modal on ESC key
  const handleEsc = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      window.closeCancelModal();
      document.removeEventListener('keydown', handleEsc);
    }
  };
  document.addEventListener('keydown', handleEsc);

  window.closeCancelModal = () => {
    modal?.remove();
  };

  window.confirmCancelBooking = async (bookingId: string) => {
    try {
      const { getTenantId } = await import('../config');
      const { getCurrentLanguage } = await import('../i18n');
      const tenantId = getTenantId();
      const language = getCurrentLanguage();

      const result = await apiFetch<{
        success: boolean;
        data: Booking;
        notifyMessage?: import('../types').LineMessage;
      }>(
        `/api/v1/${tenantId}/bookings/${bookingId}/cancel`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ language }),
        }
      );

      if (result.success) {
        // Send LINE message via LIFF if provided (no cost - user sends message)
        if (result.notifyMessage) {
          try {
            const { sendMessage } = await import('../liff');
            await sendMessage(result.notifyMessage);
            console.log('[MyBookings] Cancellation message sent via LIFF');
          } catch (msgError) {
            console.warn('[MyBookings] Failed to send cancellation message:', msgError);
            // Don't block UI if message send fails
          }
        }

        // Show custom toast instead of alert (to avoid domain name)
        showSuccessToast(t('bookingCancelledSuccess'));
        window.closeCancelModal();
        // Reload page
        initMyBookingsPage();
      } else {
        showErrorToast(t('errorCancellingBooking'));
      }
    } catch (error) {
      console.error('[MyBookings] Error cancelling booking:', error);
      showErrorToast(t('errorNetwork'));
    }
  };
}

// Extend window
declare global {
  interface Window {
    navigateToSearch: () => void;
    closeMyBookings: () => void;
    editBooking: (bookingId: string) => void;
    cancelBooking: (bookingId: string, confirmationNumber: string) => void;
    closeCancelModal: () => void;
    confirmCancelBooking: (bookingId: string) => void;
  }
}
